import type { ReactNode } from 'react';
import type { RootState } from 'src/redux/store';
import type { IContact } from 'src/pages/contacts/contacts.types';

import JsSIP from 'jssip';
import { toast } from 'sonner';
import { useBoolean } from 'minimal-shared/hooks';
import React, { useRef, useState, useEffect, useCallback } from 'react';

import { useAppSelector } from 'src/hook/hooks';
import { SIP_SERVER_URL, SIP_CALL_DOMAIN } from 'src/config';
import { useGetMediaUrlMutation } from 'src/pages/chat/slice/chatApi';
import { useFilterContactMutation } from 'src/pages/contacts/slice/contactsApi';
import { useGetInternalUserListMutation } from 'src/pages/setting/components/users/slice/userApi';

import { CallContext } from './call-context';

const SIP_SERVER = SIP_SERVER_URL;
// const SIP_ID = 'qqqqqqqqqq101@core360s.com';
// const SIP_PASSWORD = 'w8kW9bUwhNh9FEfbGJHAMGbuV';

interface SipProviderProps {
  children: ReactNode;
}

export const CallProvider: React.FC<SipProviderProps> = ({ children }) => {
  const { loginApiData, channelFeatureFlags, channelFeatureFlagsInitialized } = useAppSelector(
    (state) => (state as RootState).auth
  );
  const [filterContact] = useFilterContactMutation();
  const [getUserList] = useGetInternalUserListMutation();
  const [getMediaUrl] = useGetMediaUrlMutation();

  const [userAgent, setUserAgent] = useState<JsSIP.UA | null>(null);
  const [incomingCall, setIncomingCall] = useState<{ username: string; number: string } | null>(
    null
  );
  const [currentSession, setCurrentSession] = useState<any | null>(null);
  const [redirectTo, setRedirectTo] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [isPause, setIsPause] = useState<boolean>(false);
  const [registered, setRegistered] = useState<boolean>(false);
  const [callUserDetails, setCallUserDetails] = useState<any>(null);
  const [callDuration, setCallDuration] = useState<number>(0);
  const [isCallConnected, setIsCallConnected] = useState<boolean>(false);

  const CallDailogMethod = useBoolean();
  const CallPopupMethod = useBoolean();
  const currentSessionRef = useRef<any>(null);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const callStartTimeRef = useRef<number | null>(null);

  const startCallTimer = useCallback(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
    callStartTimeRef.current = Date.now();
    setCallDuration(0);
    setIsCallConnected(true);

    timerIntervalRef.current = setInterval(() => {
      if (callStartTimeRef.current) {
        const elapsed = Math.floor((Date.now() - callStartTimeRef.current) / 1000);
        setCallDuration(elapsed);
      }
    }, 1000);
  }, []);

  const stopCallTimer = useCallback(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    callStartTimeRef.current = null;
    setCallDuration(0);
    setIsCallConnected(false);
  }, []);

  useEffect(() => () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    }, []);

  // =======================================================
  const showNotification = useCallback((title: string, body: string, onClick?: () => void) => {
    // Guard: only run in browser and when Notification API exists
    if (typeof window !== 'undefined' && 'Notification' in window) {
      const NotificationAPI = window.Notification;

      if (NotificationAPI.permission === 'granted') {
        try {
          const notification = new NotificationAPI(title, {
            body,
            icon: '/call-icon.png',
          });

          if (onClick) {
            notification.onclick = (event) => {
              event.preventDefault();
              onClick();
            };
          }
        } catch (err) {
          // console.warn('Notifications failed in this environment', err);
          toast.success(`${title}: ${body}`);
          if (onClick) onClick();
        }
      } else {
        // console.log('Notification permission not granted');
        toast.success(`${title}: ${body}`);
        if (onClick) onClick();
      }
    } else {
      // No Notification API at all (iOS Safari / SSR)
      // console.log('Notification API not available in this browser');
      toast.success(`${title}: ${body}`);
      if (onClick) onClick();
    }
  }, []);

  // =======================================================
  const playCallSound = () => {
    let audio = document.getElementById('call-alert-audio') as HTMLAudioElement;

    if (!audio) {
      // Create the audio element if it doesn't exist
      audio = document.createElement('audio');
      audio.id = 'call-alert-audio';
      audio.src = '/ringtone/call-ringtone.mp3'; // Replace with correct path
      audio.loop = true;
      audio.preload = 'auto';
      document.body.appendChild(audio);
    }

    // Try to play the audio, handle autoplay restrictions
    audio.play().catch((err) => {
      console.warn('🔇 Audio playback failed due to autoplay policy:', err);
      // Optionally prompt user for interaction
    });
  };

  const stopCallSound = () => {
    const audio = document.getElementById('call-alert-audio') as HTMLAudioElement;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
  };

  // =======================================================
  const originalTitle = document.title;
  let blinkInterval: NodeJS.Timeout | null = null; // Store interval reference
  // =======================================================
  const [isConnecting, setIsConnecting] = useState(false);
  const isVoiceChannelAllowed =
    channelFeatureFlagsInitialized && channelFeatureFlags.VOICE === true;

  const reconnectSIP = () => {
    if (!isVoiceChannelAllowed) {
      handleResetCallDetails();
      return;
    }

    if (
      !loginApiData ||
      !('tenantId' in loginApiData) ||
      !loginApiData.tenantId ||
      !loginApiData.token ||
      !('userExtId' in loginApiData) ||
      !('userExtPassword' in loginApiData) ||
      (!loginApiData?.userExtId && !loginApiData?.userExtPassword)
    ) {
      toast.error('Webphone configuration is missing in the user profile.');
      handleResetCallDetails();
      return;
    }

    if (
      loginApiData &&
      'tenant' in loginApiData &&
      typeof loginApiData.tenant === 'object' &&
      loginApiData.tenant !== null &&
      'name' in loginApiData.tenant &&
      (loginApiData.tenant as { name?: string }).name &&
      (loginApiData.tenant as { name?: string }).name === 'MASTER'
    ) {
      handleResetCallDetails();
      return;
    }

    try {
      setIsConnecting(true); // 🔄 Process started

      if (userAgent) {
        userAgent.stop();
      }
      const socket = new JsSIP.WebSocketInterface(SIP_SERVER);

      const configuration = {
        sockets: [socket],

        // SIP URI of your extension
        uri: `sip:${loginApiData.userExtId}${SIP_CALL_DOMAIN}`,

        // SIP password
        password: loginApiData.userExtPassword,

        // WebRTC device info
        display_name: loginApiData.userExtId,
        session_timers: false,

        // Mandatory for WebRTC calling
        register: true,

        // NAT + WebRTC fixes
        pcConfig: {
          iceServers: [
            { urls: ['stun:stun.l.google.com:19302'] },
            { urls: ['stun:stun1.l.google.com:19302'] },
          ],
          iceTransportPolicy: 'all',
        },

        // Force WebRTC media constraints
        mediaConstraints: {
          audio: true,
          video: false,
        },
        user_agent: 'JsSIP',

        // Recommended extra`
        contact_uri: `sip:${loginApiData.userExtId}${SIP_CALL_DOMAIN}`,
      };

      const newUA = new JsSIP.UA(configuration);
      // console.log('🚀 ~ reconnectSIP ~ newUA:', newUA);

      const target = `${loginApiData.userExtId}${SIP_CALL_DOMAIN}`;

      newUA.sendMessage(target, '', {
        extraHeaders: [
          'Content-Type: application/sdp',
          'Allow: INVITE, ACK, CANCEL, OPTIONS, BYE, REFER, SUBSCRIBE, NOTIFY, INFO, PUBLISH, MESSAGE',
        ],
      });

      newUA.on('registered', () => {
        setRegistered(true);
        toast.success('Webphone re-connected successfully.');
        setIsConnecting(false); // ✅ Process ended
      });

      newUA.on('registrationFailed', (e: any) => {
        setRegistered(false);
        toast.error('Webphone registration failed during reconnect.');
        setIsConnecting(false); // ❌ Process ended
        if (userAgent) {
          userAgent.stop();
        }
      });

      // newUA.on('disconnected', () => {
      //   console.log('❌ newUA WebSocket disconnected');
      // });

      newUA.on('newRTCSession', (data: { session: any }) => {
        const session = data.session;

        // Ignore duplicate sessions
        if (session._isProcessed) return;
        session._isProcessed = true;

        // If already in a session and new one is incoming, reject
        if (session.direction === 'incoming') {
          if (currentSessionRef.current) {
            session.terminate({
              status_code: 486,
              reason_phrase: 'Busy Here',
            });
            return;
          }

          // Accept and track this session
          currentSessionRef.current = session;
          handleIncomingCall(session);

          // Cleanup session ref on end
          const clearSession = () => {
            currentSessionRef.current = null;
          };
          session.on('ended', clearSession);
          session.on('failed', clearSession);
        }
      });

      newUA.start();
      setUserAgent(newUA);
    } catch (error) {
      if ((error as any)?.message?.includes('Invalid value')) {
        toast.error('❌Webphone configuration is missing in the user profile.');
      } else {
        toast.error('❌ An error occurred during Webphone setup. Please try again.');
      }
      setRegistered(false);
      setIsConnecting(false); // ❌ Process ended
      if (userAgent) {
        userAgent.stop();
      }
    }
  };

  // =======================================================

  useEffect(() => {
    if (!isVoiceChannelAllowed) {
      handleResetCallDetails();
      return;
    }

    if (
      !loginApiData ||
      !('tenantId' in loginApiData) ||
      !loginApiData.tenantId ||
      !loginApiData.token ||
      !('userExtId' in loginApiData) ||
      !('userExtPassword' in loginApiData) ||
      (!loginApiData?.userExtId && !loginApiData?.userExtPassword)
    ) {
      handleResetCallDetails();
      return;
    }

    if (
      loginApiData &&
      'tenant' in loginApiData &&
      typeof loginApiData.tenant === 'object' &&
      loginApiData.tenant !== null &&
      'name' in loginApiData.tenant &&
      (loginApiData.tenant as { name?: string }).name &&
      (loginApiData.tenant as { name?: string }).name === 'MASTER'
    ) {
      handleResetCallDetails();
      return;
    }

    try {
      const socket = new JsSIP.WebSocketInterface(SIP_SERVER);
      const configuration = {
        sockets: [socket],
        uri: `${loginApiData.userExtId}${SIP_CALL_DOMAIN}`,
        password: loginApiData.userExtPassword,
      };

      const ua = new JsSIP.UA(configuration);
      setUserAgent(ua);

      ua.on('registered', () => {
        // console.log('✅ SIP Registered');
        setRegistered(true);
      });

      ua.on('registrationFailed', (e: any) => {
        // console.log('❌ Registration Failed:', e);
        if (userAgent) {
          userAgent.stop();
        }
        setRegistered(false);
      });

      // ua.on('disconnected', () => {
      //   console.log('❌ WebSocket disconnected');
      // });

      ua.on('newRTCSession', (data: { session: any }) => {
        const session = data.session;
        // console.log('🚀 ~ CallProvider ~ session:', session);

        // Ignore duplicate sessions
        if (session._isProcessed) return;
        session._isProcessed = true;

        // If already in a session and new one is incoming, reject
        if (session.direction === 'incoming') {
          if (currentSessionRef.current) {
            session.terminate({
              status_code: 486,
              reason_phrase: 'Busy Here',
            });
            return;
          }

          // Accept and track this session
          currentSessionRef.current = session;
          handleIncomingCall(session);

          // Cleanup session ref on end
          const clearSession = () => {
            currentSessionRef.current = null;
          };
          session.on('ended', clearSession);
          session.on('failed', clearSession);
        }
      });

      ua.start();
    } catch (error) {
      if ((error as any)?.message?.includes('Invalid value')) {
        toast.error('❌ Webphone configuration is missing in the user profile.');
      } else {
        // toast.error('❌ An error occurred during SIP setup. Please try again.');
      }

      setRegistered(false);
      if (userAgent) {
        userAgent.stop();
      }
    }

    // Cleanup function to stop the UA when component is unmounted or loginApiData changes
    // eslint-disable-next-line consistent-return
    return () => {
      if (userAgent) {
        userAgent.stop();
        // console.log('SIP UA stopped');
      }
    };
  }, [loginApiData, isVoiceChannelAllowed]);
  // =============================================================================
  const handleResetCallDetails = () => {
    if (userAgent) {
      userAgent.stop();
      // console.log('SIP UA stopped');
    }
    setRegistered(false);
  };
  // =============================================================================
  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      const NotificationAPI = window.Notification;
      if (NotificationAPI.permission !== 'granted') {
        NotificationAPI.requestPermission().catch((err) =>
          console.warn('Notification permission request failed', err)
        );
      }
    } else {
      // console.log('Notification API not available in this environment');
    }
  }, []);

  // ==============================================
  const getProfileUrl = async (filePath: string | null) => {
    if (filePath) {
      const { data: uploadFileUrl } = await getMediaUrl({ fileName: filePath });
      return uploadFileUrl;
    }
    return '';
  };

  const setResponseFunc = async (res: { list: IContact[]; total: number }) => {
    const updatedList = await Promise.all(
      res.list.map(async (item) => ({
        ...item,
        phone: item.phone && !item.phone.includes('+') ? `+${item.phone}` : (item.phone ?? ''),
        ProfileURL: await getProfileUrl(item.ProfileURL),
      }))
    );
    handleCallUserDetails(updatedList[0] || null);
  };
  // ==============================================
  // Outside the function (module-level or in context)
  const processedCalls = new Set<string>();

  const handleIncomingCall = async (session: any) => {
    if (currentSession) return;
    if (!session?.remote_identity?.uri?.user) return;

    const identity = session.remote_identity;
    const displayName = identity.display_name || 'Unknown';

    // Normalize phone number
    let phoneNumber = identity.uri.user;

    if (phoneNumber.startsWith('0')) {
      phoneNumber = phoneNumber.slice(1);
    }
    if (phoneNumber.length >= 10) {
      phoneNumber = phoneNumber.slice(-9); // get last 9 digits
    }

    // Skip if already processed
    if (processedCalls.has(phoneNumber)) return;
    processedCalls.add(phoneNumber);

    try {
      const contactDetails =
        phoneNumber?.length >= 5
          ? await filterContact({
              filters: [{ fieldname: 'phone', search: phoneNumber }],
              globalSearch: '',
              page: 1,
              limit: 1,
            }).unwrap()
          : await getUserList({
              filters: [{ fieldname: 'extNumber', search: phoneNumber }],
              globalSearch: '',
              page: 1,
              limit: 1,
            }).unwrap();

      if (contactDetails?.list?.length > 0) {
        setResponseFunc(contactDetails);
      }
    } catch (error) {
      console.error('Error fetching contact details:', error);
    }

    setIncomingCall({ username: displayName, number: phoneNumber });
    setCurrentSession(session);
    CallDailogMethod.onTrue();

    if (blinkInterval) clearInterval(blinkInterval);
    let blink = true;
    blinkInterval = setInterval(() => {
      document.title = blink ? '🔔 Incoming Call...' : originalTitle;
      blink = !blink;
    }, 1000);

    playCallSound();

    showNotification(
      'Incoming Call',
      `📞 Call from ${
        (callUserDetails?.firstName || '') + ' ' + (callUserDetails?.lastName || '') ||
        callUserDetails?.phone ||
        incomingCall?.username ||
        'Unknown'
      }`,
      () => {
        window.focus();
      }
    );

    session.once('ended', () => {
      handleCallEnd();
      processedCalls.clear(); // Clear after call ends
    });

    session.once('failed', () => {
      handleCallEnd();
      processedCalls.clear(); // Clear after call fails
    });
  };

  const handleCallEnd = () => {
    stopCallTimer();
    setCurrentSession(null);
    setIncomingCall(null);
    CallDailogMethod.onFalse();
    CallPopupMethod.onFalse();
    processedCalls?.clear();
    currentSessionRef.current = null;
    handleCallUserDetails(null);
    setIsMuted(false);
    setIsPause(false);
    setCallUserDetails(null);

    if (blinkInterval) clearInterval(blinkInterval);
    document.title = originalTitle;

    // showNotification('Call Ended', '🔴 The call has been disconnected.');
    // Cleanup audio elements
    document.querySelectorAll('audio').forEach((audio) => audio.remove());
  };

  const acceptCall = () => {
    if (!currentSession) {
      // console.log('⚠️ No active session to accept');
      return;
    }
    stopCallSound();

    // console.log('✅ Accepting Call...');
    currentSession.answer({
      mediaConstraints: { audio: true, video: false },
      rtcOfferConstraints: { offerToReceiveAudio: true, offerToReceiveVideo: false },
    });

    CallDailogMethod.onFalse();
    CallPopupMethod.onTrue();
    startCallTimer();

    currentSession.connection.ontrack = (event: RTCTrackEvent) => {
      // console.log('🔊 Incoming audio track received:', event.track);

      if (!event.streams.length) {
        // console.warn('⚠️ No streams found in event.');
        return;
      }

      document.querySelectorAll('audio').forEach((audio) => audio.remove());

      const remoteAudio = document.createElement('audio');
      remoteAudio.srcObject = event.streams[0];
      remoteAudio.autoplay = true;
      remoteAudio.setAttribute('playsinline', 'true');
      document.body.appendChild(remoteAudio);
    };

    currentSession.on('ended', handleCallEnd);
    currentSession.on('failed', handleCallEnd);
  };

  const rejectCall = () => {
    if (currentSession) {
      // console.log('❌ Call Rejected');
      processedCalls?.clear();
      currentSession.on('ended', handleCallEnd);
      currentSession.on('failed', handleCallEnd);
      currentSession.terminate();
      setIncomingCall(null);
      setCallUserDetails(null);
      setIsMuted(false);
      setIsPause(false);
      handleCallEnd();
      stopCallSound();
    }
  };

  const pauseCall = () => {
    if (currentSession?.isOnHold().local) {
      // console.log('⚠️ Call is already on hold');
      return;
    }

    currentSession?.hold();
    setIsPause(true);
    // console.log('⏸️ Call Paused');
  };

  const resumeCall = () => {
    if (!currentSession?.isOnHold().local) {
      // console.log('⚠️ Call is not on hold');
      return;
    }

    currentSession.unhold();
    setIsPause(false);
    // console.log('▶️ Call Resumed');
  };

  const muteCall = () => {
    if (!currentSession) {
      // console.log('⚠️ No active call to mute');
      return;
    }

    try {
      currentSession.mute({ audio: true, video: false });
    } catch (e) {
      console.warn('JsSIP mute failed:', e);
    }

    try {
      if (currentSession.connection) {
        currentSession.connection.getSenders().forEach((sender: any) => {
          if (sender.track?.kind === 'audio') sender.track.enabled = false;
        });
      }
    } catch (e) {
      console.warn('Sender track mute failed:', e);
    }

    setIsMuted(true);
    // console.log('🔇 Call Muted');
  };

  const unmuteCall = () => {
    if (!currentSession) {
      // console.log('⚠️ No active call to unmute');
      return;
    }

    try {
      currentSession.unmute({ audio: true, video: false });
    } catch (e) {
      console.warn('JsSIP unmute failed:', e);
    }

    try {
      if (currentSession.connection) {
        currentSession.connection.getSenders().forEach((sender: any) => {
          if (sender.track?.kind === 'audio') sender.track.enabled = true;
        });
      }
    } catch (e) {
      console.warn('Sender track unmute failed:', e);
    }

    setIsMuted(false);
    // console.log('🔊 Call Unmuted');
  };

  // ========================makecall=======================

  const makeCall = async (number: string, isFindDetails: boolean = false) => {
    if (!isVoiceChannelAllowed) {
      handleResetCallDetails();
      return;
    }

    if (!userAgent) {
      // console.log('❌ No UserAgent available.');
      return;
    }

    if (currentSession) {
      // console.log('⚠️ There is already an active call. Cannot make another one.');
      return;
    }

    const cleanNumber = number ? number.replace(/\s+/g, '') : '';

    const options = {
      mediaConstraints: { audio: true, video: false },

      

      rtcOfferConstraints: {
        offerToReceiveAudio: true,
        offerToReceiveVideo: false,
      },

      sessionTimers: false,

      sessionDescriptionHandlerOptions: {
        constraints: { audio: true, video: false },


        // KEEP ONLY THIS — DO NOT FORCE CODECS
        modifiers: [
          (description: any) => {
            description.sdp = description.sdp
              .replace(/a=extmap:[0-9]+ .*\n/g, '')
              .replace(/a=rtcp-rsize\r?\n/g, '');

            return description;
          },
        ],
      },
    };

    // console.log(`📞 Dialing: ${cleanNumber}`);
    if (isFindDetails) {
      try {
        const contactDetails =
          cleanNumber?.length >= 5
            ? await filterContact({
                filters: [{ fieldname: 'phone', search: cleanNumber }],
                globalSearch: '',
                page: 1,
                limit: 1,
              }).unwrap()
            : await getUserList({
                filters: [{ fieldname: 'extNumber', search: cleanNumber }],
                globalSearch: '',
                page: 1,
                limit: 1,
              }).unwrap();

        if (contactDetails?.list?.length > 0) {
          setResponseFunc(contactDetails);
        }
      } catch (error) {
        console.error('Error fetching contact details:', error);
      }
    }

    const updateNumber = cleanNumber;
    // const updateNumber =
    //   number?.length >= 10 && number.startsWith('0') ? number.replace(/^0/, '972') : number;

    const session = userAgent.call(`sip:${cleanNumber}${SIP_CALL_DOMAIN}`, options);

    setCurrentSession(session);
    setIncomingCall({
      username: session.remote_identity.display_name || 'Unknown',
      number: session.remote_identity.uri.user,
    });
    CallPopupMethod.onTrue();
    // console.log('🚀 ~ makeCall ~ session:', session.data);

    session.on('accepted', () => {
      startCallTimer();
    });

    showNotification('Outgoing Call', `📤 Calling ${updateNumber}...`, () => {
      window.focus(); // Bring focus to the app
    });

    session.connection.ontrack = (event: RTCTrackEvent) => {
      if (!event.streams.length) return;

      document.querySelectorAll('audio').forEach((audio) => audio.remove());
      const remoteAudio = document.createElement('audio');
      remoteAudio.srcObject = event.streams[0];
      remoteAudio.autoplay = true;
      remoteAudio.setAttribute('playsinline', 'true');
      document.body.appendChild(remoteAudio);
    };

    session.on('ended', handleCallEnd);
    session.on('failed', handleCallEnd);
  };

  const transferCall = (target: string) => {
    if (currentSession && currentSession.isEstablished()) {
      try {
        const referSubscriber = currentSession.refer(target);

        // Optional: show "transferring" status in UI
        // console.log(`🔁 Transferring call to ${target}...`);

        referSubscriber.on('accepted', () => {
          // console.log('✅ Call transfer accepted');
          handleCallEnd(); // Clean up UI state, timers, etc.
          currentSession.terminate(); // Terminate only after transfer accepted
        });

        referSubscriber.on('failed', (e: any) => {
          // console.error('❌ Call transfer failed', e);
          // Optionally inform the user or retry
        });
      } catch (error) {
        console.error('🚫 Transfer failed due to exception:', error);
      }
    } else {
      console.warn('⚠️ No active call to transfer.');
    }
  };

  const sendDTMF = (digit: string) => {
    // console.log('🚀 ~ sendDTMF ~ digit:', digit);
    if (!currentSession || !currentSession.isEstablished()) return;

    // send DTMF tone (e.g., '1', '2', '*', '#')
    currentSession.sendDTMF(digit);
  };
  // =============================================================================================================================================================
  const handleCallUserDetails = (contactDetails: any) => {
    setCallUserDetails(contactDetails);
  };

  // =============================================================================================================================================================
  // =============================================================================================================================================================
  // ✅ SOCKET CODE REMOVED - Now using shared WebSocket context via useNotificationHandler hook
  // Previously had 2 separate socket.io connections here (lines 779-902, ~130 lines removed)
  // All socket events are now handled by:
  //   - websocket-service.ts (centralized event handling)
  //   - use-notification-handler.ts (notification-specific logic)
  // =============================================================================================================================================================

  // =============================================================================================================================================================
  const [selectedNotificationId, setSelectedNotificationId] = useState<string | null>(null);
  const handleNotification = (id: string) => {
    setSelectedNotificationId(id);
  };
  const handleResetNotificationId = () => {
    setSelectedNotificationId(null);
  };

  // =============================================================================================================================================================

  const disconnectSIP = () => {
    try {
      if (userAgent) {
        userAgent.stop(); // stops UA + disconnects WS
        setUserAgent(null);
      }
      if (currentSessionRef.current) {
        currentSessionRef.current.terminate();
        currentSessionRef.current = null;
      }
      setRegistered(false);
      handleResetCallDetails();
      // console.log("🔌 SIP Disconnected");
    } catch (err) {
      // console.error('Failed to disconnect SIP:', err);
    }
  };

  // Listener
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'DISCONNECT_SIP') {
        disconnectSIP();
      }
    };
    window.addEventListener('message', handler);

    return () => window.removeEventListener('message', handler);
  }, []);

  // =============================================================================================================================================================

  return (
    <CallContext.Provider
      value={{
        userAgent,
        incomingCall,
        redirectTo,
        registered,
        makeCall,
        acceptCall,
        rejectCall,
        pauseCall,
        resumeCall,
        muteCall,
        unmuteCall,
        CallDailogMethod,
        CallPopupMethod,
        isMuted,
        isPause,
        transferCall,
        sendDTMF,
        handleNotification,
        selectedNotificationId,
        handleResetNotificationId,
        reconnectSIP,
        isConnecting,
        handleResetCallDetails,
        handleCallUserDetails,
        callUserDetails,
        disconnectSIP,
        callDuration,
        isCallConnected,
      }}
    >
      {children}
    </CallContext.Provider>
  );
};
